import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';
import { IBulkApprovalActionDto } from '../../pages/TravelRequest/constants/travelreq.interface';
import { ExportActions, SystemApprovalAction } from '../../enums';
import { LogsSortByOptions } from '../../pages/TravelRequest/TravelReqDetail';
import {
  ICreateProcessPayload,
  ICreateSubProcessPayload,
  ICreateActivityPayload,
  ICreateActivityUserPayload,
  IReassignActivityUserPayload,
  IReassignActivityUserResponse,
  IUpdateActivityUserPayload,
  IGetActivityUserParams,
  IActivityRemarkPayload,
  IUpdateExecutionPayload,
  IMarkActivityStatusPayload,
} from '../../pages/ActivityTracking/constants/activity.interface';

export type ActivityHierarchyEntity =
  | 'project'
  | 'process'
  | 'subprocess'
  | 'activity'
  | 'mapping'
  | 'assignment';

export interface ActivityHierarchyDeleteImpact {
  entity: ActivityHierarchyEntity;
  id: number;
  name: string;
  allowed: boolean;
  blockingReasons: string[];
  impact: {
    activeChildren: number;
    activeMappings: number;
    activeAssignments: number;
    incompleteExecutions: number;
    historicalExecutions: number;
    dependentMappings: number;
  };
}

const activityTrackingApi = createApi({
  reducerPath: 'activityTracking',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Project',
    'Process',
    'SubProcess',
    'Activity',
    'ActivityUser',
    'ActivityApprovalMapping',
    'ActivityAssignment',
    'Plant',
    'Frequency',
    'ActivityRemarks',
    'Dashboard',
    'Reports',
    'ActivityOwner',
    'ResetApprovalHierarchy',
    'RearrangeApprovalHierarchy',
    'ActivityMasterAttachment',
    // Changelog caches (invalidated on update so audit trail always refetches fresh data)
    'ProcessChangeLog',
    'SubProcessChangeLog',
    'ActivityMasterChangeLog',
    'ActivityOwnerChangeLog',
    'ActivityMappingChangeLog',
    'ProjectChangeLog',
  ],
  endpoints: builder => ({
    // ================= PROJECT =================
    getProjectField: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: query => ({
        url: `/activity-master/project${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['Project'],
    }),

    createProjectField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/activity-master/project`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['Project']),
    }),

    updateProjectField: builder.mutation<
      BackendResponse<void>,
      { id: number; [key: string]: any }
    >({
      query: ({ id, ...rest }) => ({
        url: `/activity-master/project/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error, arg) =>
        error ? [] : ['Project', { type: 'ProjectChangeLog', id: arg.id }],
    }),

    deleteProjectField: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: ids => ({
        url: `/activity-master/project`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['Project']),
    }),

    getProjectChangeLogs: builder.query<
      BackendResponse<{ list: any[]; count: number }>,
      { id: number; page?: number; pageSize?: number; sortBy?: string }
    >({
      query: ({ id, page = 1, pageSize = 25, sortBy = 'desc' }) => ({
        url: `/activity-master/project/changelogs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (_r, _e, arg) => [{ type: 'ProjectChangeLog', id: arg.id }],
      keepUnusedDataFor: 0,
    }),
    // ================= PROCESS =================
    getProcessField: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: query => ({
        url: `/activity-master/process${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['Process'],
    }),

    createProcessField: builder.mutation<
      BackendResponse<any>,
      ICreateProcessPayload
    >({
      query: payload => ({
        url: `/activity-master/process`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['Process']),
    }),

    updateProcessField: builder.mutation<
      BackendResponse<void>,
      { id: number; [key: string]: any }
    >({
      query: ({ id, ...rest }) => ({
        url: `/activity-master/process/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error, arg) =>
        error ? [] : ['Process', { type: 'ProcessChangeLog', id: arg.id }],
    }),

    deleteProcessField: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: ids => ({
        url: `/activity-master/process`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['Process']),
    }),

    getProcessChangeLogs: builder.query<
      BackendResponse<{ list: any[]; count: number }>,
      { id: number; page?: number; pageSize?: number; sortBy?: string }
    >({
      query: ({ id, page = 1, pageSize = 25, sortBy = 'desc' }) => ({
        url: `/activity-master/process/changelogs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (_r, _e, arg) => [{ type: 'ProcessChangeLog', id: arg.id }],
      keepUnusedDataFor: 0,
    }),

    // ================= SUB PROCESS =================
    getSubProcessField: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: query => ({
        url: `/activity-master/sub-process${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['SubProcess'],
    }),

    createSubProcessField: builder.mutation<
      BackendResponse<any>,
      ICreateSubProcessPayload
    >({
      query: payload => ({
        url: `/activity-master/sub-process`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['SubProcess']),
    }),

    updateSubProcessField: builder.mutation<
      BackendResponse<void>,
      { id: number; [key: string]: any }
    >({
      query: ({ id, ...rest }) => ({
        url: `/activity-master/sub-process/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error, arg) =>
        error
          ? []
          : ['SubProcess', { type: 'SubProcessChangeLog', id: arg.id }],
    }),

    deleteSubProcessField: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: ids => ({
        url: `/activity-master/sub-process`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['SubProcess']),
    }),

    getSubProcessChangeLogs: builder.query<
      BackendResponse<{ list: any[]; count: number }>,
      { id: number; page?: number; pageSize?: number; sortBy?: string }
    >({
      query: ({ id, page = 1, pageSize = 25, sortBy = 'desc' }) => ({
        url: `/activity-master/sub-process/changelogs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'SubProcessChangeLog', id: arg.id },
      ],
      keepUnusedDataFor: 0,
    }),

    // ================= ACTIVITY =================
    getActivityField: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: query => ({
        url: `/activity-master/activity${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['Activity'],
    }),

    createActivityField: builder.mutation<
      BackendResponse<any>,
      ICreateActivityPayload | FormData | any
    >({
      query: payload => ({
        url: `/activity-master/activity`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['Activity']),
    }),
    updateActivityField: builder.mutation<
      BackendResponse<void>,
      { id: number; [key: string]: any }
    >({
      query: ({ id, ...rest }) => ({
        url: `/activity-master/activity/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error, arg) =>
        error
          ? []
          : ['Activity', { type: 'ActivityMasterChangeLog', id: arg.id }],
    }),

    deleteActivityField: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: ids => ({
        url: `/activity-master/activity`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['Activity']),
    }),

    getActivityMasterChangeLogs: builder.query<
      BackendResponse<{ list: any[]; count: number }>,
      { id: number; page?: number; pageSize?: number; sortBy?: string }
    >({
      query: ({ id, page = 1, pageSize = 25, sortBy = 'desc' }) => ({
        url: `/activity-master/activity/changelogs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'ActivityMasterChangeLog', id: arg.id },
      ],
      keepUnusedDataFor: 0,
    }),

    getPlantField: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: query => ({
        url: `/activity-master/plant${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['Plant'],
    }),
    // ================= OWNER =================
    getActivityOwnerField: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: query => ({
        url: `/activity-master/owner${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['ActivityOwner'],
    }),

    createActivityOwnerField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/activity-master/owner`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ActivityOwner']),
    }),

    updateActivityOwnerField: builder.mutation<
      BackendResponse<void>,
      { id: number; [key: string]: any }
    >({
      query: ({ id, ...rest }) => ({
        url: `/activity-master/owner/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error, arg) =>
        error
          ? []
          : ['ActivityOwner', { type: 'ActivityOwnerChangeLog', id: arg.id }],
    }),

    deleteActivityOwnerField: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: ids => ({
        url: `/activity-master/owner`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ActivityOwner']),
    }),
    getActivityOwnerChangeLogs: builder.query<
      BackendResponse<{ list: any[]; count: number }>,
      { id: number; page?: number; pageSize?: number; sortBy?: string }
    >({
      query: ({ id, page = 1, pageSize = 25, sortBy = 'desc' }) => ({
        url: `/activity-master/owner/changelogs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'ActivityOwnerChangeLog', id: arg.id },
      ],
      keepUnusedDataFor: 0,
    }),
    getActivityDependencies: builder.query<BackendResponse<any[]>, number>({
      query: id => `/activity/activity/${id}/dependencies`,
      providesTags: ['Activity'],
    }),

    updateActivityDependencies: builder.mutation<
      BackendResponse<void>,
      { id: number; dependencies: any[] }
    >({
      query: ({ id, dependencies }) => ({
        url: `/activity/activity/${id}/dependencies`,
        method: 'POST',
        body: { dependencies },
      }),
      invalidatesTags: ['Activity'],
    }),
    getDependencyTable: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: query => ({
        url: `/activity/activity-dependencies/list${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['Activity'],
    }),
    getActivityUser: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      IGetActivityUserParams
    >({
      query: params => ({
        url: '/activity/activity-user',
        params,
      }),
      providesTags: ['ActivityUser'],
    }),

    createActivityUser: builder.mutation<
      BackendResponse<any>,
      ICreateActivityUserPayload
    >({
      query: body => ({
        url: '/activity/activity-user',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ActivityUser'],
    }),
    reassignActivityUser: builder.mutation<
      BackendResponse<IReassignActivityUserResponse>,
      IReassignActivityUserPayload
    >({
      query: body => ({
        url: '/activity/activity-user-reassign',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, error, argument) =>
        error
          ? []
          : [
              'ActivityUser',
              'Dashboard',
              'Reports',
              {
                type: 'ActivityApprovalMapping',
                id: argument.assignmentId,
              },
            ],
    }),

    updateActivityUser: builder.mutation<
      BackendResponse<any>,
      IUpdateActivityUserPayload
    >({
      query: ({ id, ...body }) => ({
        url: `/activity/activity-user/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ActivityUser'],
    }),

    deleteActivityUser: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: ids => ({
        url: '/activity/activity-user',
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: ['ActivityUser'],
    }),
    getMyActivity: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      IGetActivityUserParams
    >({
      query: params => ({
        url: '/activity/my-activity',
        params,
      }),
      providesTags: ['ActivityUser'],
    }),
    markAllComplete: builder.mutation<BackendResponse<any>, any>({
      query: data => ({
        url: '/activity/activity-mark-as-complete',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ActivityUser'],
    }),

    markInProgress: builder.mutation<BackendResponse<any>, any>({
      query: data => ({
        url: '/activity/activity-mark-in-progress',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ActivityUser'],
    }),

    markOneComplete: builder.mutation<
      BackendResponse<any>,
      IMarkActivityStatusPayload
    >({
      query: value => ({
        url: '/activity/activity-mark-one-as-complete',
        method: 'POST',
        body: value,
      }),
      invalidatesTags: ['ActivityUser'],
    }),

    markOneInProgress: builder.mutation<
      BackendResponse<any>,
      IMarkActivityStatusPayload
    >({
      query: value => ({
        url: '/activity/activity-mark-one-in-progress',
        method: 'POST',
        body: value,
      }),
      invalidatesTags: ['ActivityUser'],
    }),

    createActivityApprovalMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/activity/approval-config`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['ActivityApprovalMapping'];
      },
    }),
    updateActivityApprovalMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        const { id, ...rest } = payload;
        return {
          url: `/activity/approval-config/${id}`,
          method: 'PATCH',
          body: rest,
        };
      },
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return [
          'ActivityApprovalMapping',
          { type: 'ActivityApprovalMapping', id: +id },
        ];
      },
    }),

    getActivityApprovalMapping: builder.query<
      BackendResponse<any>,
      Partial<{
        page: number;
        pageSize: number;
        userId: number;
      }>
    >({
      query: params => ({
        url: `/activity/approval-config${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['ActivityApprovalMapping'],
    }),

    getActivityApprovalMappingById: builder.query<BackendResponse<any>, number>(
      {
        query: id => ({
          url: `/activity/approval-config/${id}`,
          method: 'GET',
        }),
        providesTags: (__, err, id) =>
          err ? [] : [{ type: 'ActivityApprovalMapping', id }],
      },
    ),

    getActivityApprovalHierarchy: builder.query<
      Blob,
      Partial<{
        page: number;
        pageSize: number;
        userId: number;
      }>
    >({
      query: params => ({
        url: `/activity/approval-hierarchy${parseQueryParams(params)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      providesTags: ['ActivityApprovalMapping'],
    }),
    deleteActivityApprovalMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/activity/approval-config${parseQueryParams(payload)}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['ActivityApprovalMapping'];
      },
    }),
    getActivityApprovals: builder.query<
      BackendResponse<any>,
      Partial<{
        page: number;
        pageSize: number;
      }>
    >({
      query: params => ({
        url: `/activity-approvals${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['ActivityApprovalMapping'],
    }),
    getActivityApprovalDetails: builder.query<BackendResponse<any>, number>({
      query: id => ({
        url: `/activity-approvals/detail/${id}`,
        method: 'GET',
      }),
      providesTags: (__, err, id) =>
        err ? [] : [{ type: 'ActivityApprovalMapping', id: id }],
    }),
    bulkApproveActivity: builder.mutation<
      { message: string; partialFailure?: boolean },
      IBulkApprovalActionDto
    >({
      query: payload => ({
        url: '/activity-approvals/bulk',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['ActivityApprovalMapping'],
    }),
    ApproveActivity: builder.mutation<BackendResponse<void>, any>({
      query: payload => ({
        url: `/activity-approvals`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['ActivityApprovalMapping'],
    }),
    approveActivityFromEmail: builder.mutation<
      BackendResponse<{ action: SystemApprovalAction }>,
      { token: string } | Record<string, unknown>
    >({
      query: payload => {
        return {
          url: `/activity-approvals/approve-from-mail`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: (_, error) => (error ? [] : ['ActivityApprovalMapping']),
    }),
    getActivityDetails: builder.query<
      BackendResponse<any>,
      { id: number; year?: number; month?: number }
    >({
      query: ({ id, year, month }) => ({
        url: `/activity/detail/${id}`,
        method: 'GET',
        params: {
          year,
          month,
        },
      }),
      providesTags: (__, err, arg) =>
        err ? [] : [{ type: 'ActivityApprovalMapping', id: arg.id }],
    }),

    // ================= ACTIVITY COMPANY ASSIGNMENT =================

    getActivityAssignments: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: params => ({
        url: '/activity/activity-mapping',
        params,
      }),
      providesTags: ['ActivityAssignment'],
    }),

    getHierarchyDeleteImpact: builder.query<
      BackendResponse<ActivityHierarchyDeleteImpact>,
      { entity: ActivityHierarchyEntity; id: number }
    >({
      query: ({ entity, id }) => ({
        url: `/activity/hierarchy/${entity}/${id}/delete-impact`,
        method: 'GET',
      }),
    }),

    deleteHierarchyNode: builder.mutation<
      BackendResponse<ActivityHierarchyDeleteImpact & { removed: true }>,
      { entity: ActivityHierarchyEntity; id: number }
    >({
      query: ({ entity, id }) => ({
        url: `/activity/hierarchy/${entity}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) =>
        error
          ? []
          : [
              'Project',
              'Process',
              'SubProcess',
              'Activity',
              'ActivityAssignment',
              'ActivityUser',
              'Dashboard',
            ],
    }),

    createActivityAssignment: builder.mutation<BackendResponse<any>, any>({
      query: body => ({
        url: '/activity/activity-mapping',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ActivityAssignment'],
    }),

    deleteActivityAssignment: builder.mutation<any, number[]>({
      query: ids => ({
        url: '/activity/activity-mapping',
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: ['ActivityAssignment'],
    }),
    updateActivityAssignment: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        const { id, ...rest } = payload;
        return {
          url: `/activity/activity-mapping/${id}`,
          method: 'PATCH',
          body: rest,
        };
      },
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        const nid = +id;
        return [
          'ActivityAssignment',
          { type: 'ActivityAssignment', id: nid },
          { type: 'ActivityMappingChangeLog', id: nid },
        ];
      },
    }),

    getFrequency: builder.query<any, any>({
      query: () => ({
        url: '/activity-master/frequency',
      }),
      providesTags: ['Frequency'],
    }),

    getActivityMappingDetails: builder.query<BackendResponse<any>, number>({
      query: id => ({
        url: `/activity/activity-mapping-detail/${id}`,
        method: 'GET',
      }),
      providesTags: (__, err, id) =>
        err ? [] : [{ type: 'ActivityAssignment', id }],
    }),
    getActivityMappingChangeLogs: builder.query<
      BackendResponse<{ list: any[]; count: number }>,
      { id: number; page?: number; pageSize?: number; sortBy?: string }
    >({
      query: ({ id, page = 1, pageSize = 25, sortBy = 'desc' }) => ({
        url: `/activity/activity-mapping/changelogs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'ActivityMappingChangeLog', id: arg.id },
      ],
      keepUnusedDataFor: 0,
    }),

    uploadActivityAttachment: builder.mutation<
      BackendResponse<any>,
      {
        year: string;
        month: string;
        assignmentId: number;
        file: File;
        remarks?: string;
        documentType: string;
      }
    >({
      query: ({ assignmentId, file, remarks, documentType, year, month }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', documentType);
        formData.append('year', year);
        formData.append('month', month);
        if (remarks) {
          formData.append('remarks', remarks);
        }

        return {
          url: `/activity-attachments/${assignmentId}/upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, error, { assignmentId }) => {
        if (error) {
          return [];
        }
        return [
          'ActivityAssignment',
          { type: 'ActivityAssignment', id: +assignmentId },
        ];
      },
    }),

    getActivityAttachments: builder.query<BackendResponse<any>, number>({
      query: assignmentId => ({
        url: `/activity-attachments/${assignmentId}/all`,
        method: 'GET',
      }),
      providesTags: (__, err, id) =>
        err ? [] : [{ type: 'ActivityAssignment', id }],
    }),
    getActivityAttachmentFile: builder.query<
      Blob,
      { assignmentId: number; attachmentId: number }
    >({
      query: ({ assignmentId, attachmentId }) => ({
        url: `/activity-attachments/${assignmentId}/file/${attachmentId}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      providesTags: (__, err, { assignmentId }) =>
        err ? [] : [{ type: 'ActivityAssignment', id: assignmentId }],
    }),

    // Activity Master attachments (under activity-master)
    getActivityMasterAttachments: builder.query<BackendResponse<any>, number>({
      query: activityId => ({
        url: `/activity-master/attachment/${activityId}/all`,
        method: 'GET',
      }),
      providesTags: (__, err, activityId) =>
        err ? [] : [{ type: 'ActivityMasterAttachment', id: activityId }],
    }),
    uploadActivityMasterAttachment: builder.mutation<
      BackendResponse<any>,
      { activityId: number; file: File; remarks?: string; documentType: string }
    >({
      query: ({ activityId, file, remarks, documentType }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', documentType);
        if (remarks) formData.append('remarks', remarks);
        return {
          url: `/activity-master/${activityId}/upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, error, { activityId }) =>
        error ? [] : [{ type: 'ActivityMasterAttachment', id: activityId }],
    }),
    getActivityMasterAttachmentFile: builder.query<
      Blob,
      { activityId: number; attachmentId: number }
    >({
      query: ({ activityId, attachmentId }) => ({
        url: `/activity-master/${activityId}/file/${attachmentId}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),
    updateActivityMasterAttachment: builder.mutation<
      BackendResponse<any>,
      {
        activityId: number;
        attachmentId: number;
        documentType?: string;
        remarks?: string;
      }
    >({
      query: ({ activityId, attachmentId, documentType, remarks }) => ({
        url: `/activity-master/${activityId}/attachment/${attachmentId}`,
        method: 'PATCH',
        body: { documentType, remarks },
      }),
      invalidatesTags: (_, error, { activityId }) =>
        error ? [] : [{ type: 'ActivityMasterAttachment', id: activityId }],
    }),
    deleteActivityMasterAttachment: builder.mutation<
      BackendResponse<any>,
      { activityId: number; attachmentId: number }
    >({
      query: ({ activityId, attachmentId }) => ({
        url: `/activity-master/${activityId}/attachment/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, { activityId }) =>
        error ? [] : [{ type: 'ActivityMasterAttachment', id: activityId }],
    }),

    getActivityRemarks: builder.query<BackendResponse<any>, number>({
      query: id => ({
        url: `/activity/remarks/${id}`,
        method: 'GET',
      }),
      providesTags: (__, err, id) =>
        err ? [] : [{ type: 'ActivityRemarks', id }],
    }),
    createActivityRemarks: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        const { id, ...rest } = payload;
        return {
          url: `/activity/remarks/${id}`,
          method: 'POST',
          body: rest,
        };
      },
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return ['ActivityRemarks', { type: 'ActivityRemarks', id: +id }];
      },
    }),
    createActivityRemarksForApprover: builder.mutation<
      BackendResponse<any>,
      any
    >({
      query: payload => {
        const { id, ...rest } = payload;
        return {
          url: `/activity/remarks-approver/${id}`,
          method: 'POST',
          body: rest,
        };
      },
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return ['ActivityRemarks', { type: 'ActivityRemarks', id: +id }];
      },
    }),

    // dashboard
    getDashboardRecentActivity: builder.query<BackendResponse<any>, any>({
      query: params => ({
        url: '/activity-dashboard/recent-activity',
        params,
      }),
      providesTags: ['Dashboard'],
    }),

    getDashboardTileData: builder.query<BackendResponse<any>, any>({
      query: params => ({
        url: '/activity-dashboard/tile',
        params,
      }),
      providesTags: ['Dashboard'],
    }),

    getDashboardProductivityData: builder.query<BackendResponse<any>, any>({
      query: params => ({
        url: '/activity-dashboard/productivity',
        params,
      }),
      providesTags: ['Dashboard'],
    }),

    getDashboardUserSummaryData: builder.query<BackendResponse<any>, any>({
      query: params => ({
        url: '/activity-dashboard/user-summary',
        params,
      }),
      providesTags: ['Dashboard'],
    }),

    getDashboardProcessFlowData: builder.query<BackendResponse<any>, any>({
      query: params => ({
        url: '/activity-dashboard/flow',
        params,
      }),
      providesTags: ['Dashboard'],
    }),

    getActivityReports: builder.query<
      BackendResponse<any>,
      Partial<{
        page: number;
        pageSize: number;
      }>
    >({
      query: params => ({
        url: `/activity-reports/reports${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['Reports'],
    }),

    updateExecution: builder.mutation<
      BackendResponse<void>,
      IUpdateExecutionPayload
    >({
      query: ({ id, ...rest }) => ({
        url: `/activity/execution-update/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['Process']),
    }),

    getActivityApprovalLogsById: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy: LogsSortByOptions }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/activity-approvals/logs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'Activity', id }],
    }),
    getActivityChangeLogsById: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy: LogsSortByOptions }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/activity-reports/changelogs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'Activity', id }],
    }),
    getActivitySapLogsById: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy: LogsSortByOptions }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/activity-reports/sap-logs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'Activity', id }],
    }),
    printActivityRequest: builder.query<Blob, { id: number }>({
      query: ({ id }) => ({
        url: `/activity-reports/print/${id}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      providesTags: (_, __, { id }) => [{ type: 'Reports', id }],
    }),

    downloadActivityRequestReport: builder.query<
      Blob,
      { action: ExportActions; query: any }
    >({
      query: ({ action, query }) => ({
        url: `/activity-reports/export/${action}${parseQueryParams(query)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    resetApprovalHierarchy: builder.mutation<
      BackendResponse<void>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/activity-approvals/reset-approval-hierarchy/${id}`,
        method: 'GET',
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ResetApprovalHierarchy']),
    }),

    rearrangeApprovalHierarchy: builder.mutation<
      BackendResponse<any>,
      { id: string; mappings: any }
    >({
      query: ({ id, mappings }) => ({
        url: `/activity-approvals/rearrange-approval-hierarchy/${id}`,
        method: 'POST',
        body: mappings,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['RearrangeApprovalHierarchy'],
    }),

    pushActivityToErp: builder.mutation<
      BackendResponse<any>,
      { id: number; variant?: string }
    >({
      query: ({ id, variant }) => ({
        url: `/activity-reports/push/erp`,
        method: 'POST',
        body: { id, variant },
      }),
      invalidatesTags: ['Activity'],
    }),
  }),
});

export const {
  // Project Hooks
  useGetProjectFieldQuery,
  useCreateProjectFieldMutation,
  useUpdateProjectFieldMutation,
  useDeleteProjectFieldMutation,
  useGetProjectChangeLogsQuery,
  // Process Hooks
  useGetProcessFieldQuery,
  useCreateProcessFieldMutation,
  useUpdateProcessFieldMutation,
  useDeleteProcessFieldMutation,
  useGetProcessChangeLogsQuery,
  // Sub-Process Hooks
  useGetSubProcessFieldQuery,
  useCreateSubProcessFieldMutation,
  useUpdateSubProcessFieldMutation,
  useDeleteSubProcessFieldMutation,
  useGetSubProcessChangeLogsQuery,
  // Activity Hooks
  useGetActivityFieldQuery,
  useCreateActivityFieldMutation,
  useUpdateActivityFieldMutation,
  useDeleteActivityFieldMutation,
  useGetActivityMasterChangeLogsQuery,

  // Activity Owner Hooks
  useGetActivityOwnerFieldQuery,
  useCreateActivityOwnerFieldMutation,
  useUpdateActivityOwnerFieldMutation,
  useDeleteActivityOwnerFieldMutation,
  useGetActivityOwnerChangeLogsQuery,

  useGetActivityDependenciesQuery,
  useUpdateActivityDependenciesMutation,
  useGetDependencyTableQuery,

  useGetActivityUserQuery,
  useCreateActivityUserMutation,
  useUpdateActivityUserMutation,
  useDeleteActivityUserMutation,
  useReassignActivityUserMutation,

  useGetMyActivityQuery,
  useMarkAllCompleteMutation,
  useMarkInProgressMutation,
  useMarkOneCompleteMutation,
  useMarkOneInProgressMutation,

  useGetActivityApprovalMappingQuery,
  useCreateActivityApprovalMappingMutation,
  useUpdateActivityApprovalMappingMutation,
  useGetActivityApprovalMappingByIdQuery,
  useLazyGetActivityApprovalHierarchyQuery,
  useDeleteActivityApprovalMappingMutation,

  useGetActivityApprovalsQuery,
  useBulkApproveActivityMutation,
  useApproveActivityMutation,
  useGetActivityDetailsQuery,
  useGetActivityApprovalDetailsQuery,

  useGetActivityAssignmentsQuery,
  useLazyGetHierarchyDeleteImpactQuery,
  useDeleteHierarchyNodeMutation,
  useCreateActivityAssignmentMutation,
  useDeleteActivityAssignmentMutation,
  useUpdateActivityAssignmentMutation,
  useGetActivityMappingDetailsQuery,
  useGetActivityMappingChangeLogsQuery,

  useGetPlantFieldQuery,
  useGetFrequencyQuery,

  useUploadActivityAttachmentMutation,
  useGetActivityAttachmentsQuery,
  useGetActivityAttachmentFileQuery,
  useLazyGetActivityAttachmentFileQuery,

  useGetActivityMasterAttachmentsQuery,
  useUploadActivityMasterAttachmentMutation,
  useUpdateActivityMasterAttachmentMutation,
  useDeleteActivityMasterAttachmentMutation,
  useLazyGetActivityMasterAttachmentFileQuery,

  useGetActivityRemarksQuery,
  useCreateActivityRemarksMutation,
  useCreateActivityRemarksForApproverMutation,

  useGetDashboardRecentActivityQuery,
  useGetDashboardTileDataQuery,
  useGetDashboardProductivityDataQuery,
  useGetDashboardUserSummaryDataQuery,
  useGetDashboardProcessFlowDataQuery,

  useGetActivityReportsQuery,

  useUpdateExecutionMutation,

  useApproveActivityFromEmailMutation,

  useGetActivityApprovalLogsByIdQuery,
  useGetActivityChangeLogsByIdQuery,
  useGetActivitySapLogsByIdQuery,

  useLazyPrintActivityRequestQuery,
  useLazyDownloadActivityRequestReportQuery,

  useRearrangeApprovalHierarchyMutation,
  useResetApprovalHierarchyMutation,

  usePushActivityToErpMutation,
} = activityTrackingApi;

export default activityTrackingApi;
